import { Signal } from '@angular/core';
import { maxLength, minLength, PathKind, SchemaPath, SchemaPathRules } from '@angular/forms/signals';
import { InputFieldPropertyState } from '../state/input-field-properties/input-field-properties-collection.state';

/** Options of the {@link inputFieldLength} rule. */
export interface InputFieldLengthConfig {
    /** message reported when the text is shorter than the backend's minimum */
    minLengthMessage?: string;
    /** message reported when the text is longer than the backend's maximum */
    maxLengthMessage?: string;
}

/**
 * A backend length bound as a signal-forms rule reads it: `undefined` switches the rule off.
 *
 * Mirrors `ModelOptionsLoaderService.load`, which the legacy models go through: a bound the
 * backend did not send (`NaN`, after the converter's `parseInt` of an empty string) applies no
 * rule, and a bound of zero or less means "unlimited" rather than "nothing may be typed".
 *
 * @param bound the backend's bound, absent when there is no property for the field
 * @returns the bound to enforce, or `undefined` when there is none
 */
function lengthBound(bound: number | undefined): number | undefined {
    return bound !== undefined && !isNaN(bound) && bound > 0 ? bound : undefined;
}

/**
 * Signal-forms schema rule applying the text length bounds the backend configures for a field
 * through its input field property — the signal-forms counterpart of the length half of
 * `ModelOptionsLoaderService.load`.
 *
 * The property is read through a signal, so the bounds follow the input-field-properties slice
 * when a sync reloads it, and a field the backend has no property for is left unbounded.
 *
 * @param path the string path to bound
 * @param property the field's input field property, `undefined` when the backend has none
 * @param config optional messages for each bound
 */
export function inputFieldLength<TPathKind extends PathKind = PathKind.Root>(
    path: SchemaPath<string, SchemaPathRules.Supported, TPathKind>,
    property: Signal<InputFieldPropertyState | undefined>,
    config?: InputFieldLengthConfig,
): void {
    minLength(path, () => lengthBound(property()?.minLength), { message: config?.minLengthMessage });
    maxLength(path, () => lengthBound(property()?.maxLength), { message: config?.maxLengthMessage });
}
